import EmailAttachmentsField, {
  type EmailAttachmentControlProps,
} from '@/components/affiliate/bulk-email/EmailAttachmentsField';
import { ACCEPTED_TYPES } from '@/components/affiliate/bulk-email/editor-config';
import { EMAIL_ATTACHMENT_LIMITS } from '@fluenceur/types';
import { Box, type BoxProps, HStack, VStack } from '@chakra-ui/react';
import { LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { $generateHtmlFromNodes } from '@lexical/html';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode } from '@lexical/rich-text';
import type { EditorState, LexicalEditor } from 'lexical';
import { $getRoot } from 'lexical';
import type { ReactElement, ReactNode } from 'react';
import { memo, useCallback, useMemo, useRef } from 'react';
import {
  InitialStatePlugin,
  VariableDropdownPlugin,
  VariableNode,
  VariableTypeaheadPlugin,
  type AvailableVariable,
} from '@fluenceur/ui';
import { placeCaretFromFieldGutter } from './field-gutter-caret';
import { ToolbarPlugin } from './plugins/toolbar-plugin';

type RichTextMessageEditorVariant = 'card' | 'document';

export interface RichTextMessageEditorChange {
  editorState: EditorState;
  editor: LexicalEditor;
  plainText: string;
  html?: string;
}

interface RichTextMessageEditorProps {
  editorKey: string | number;
  namespace: string;
  initialContent?: string | null;
  availableVariables: AvailableVariable[];
  onContentChange: (change: RichTextMessageEditorChange) => void;
  placeholder: string;
  ariaLabel: string;
  insertVariableLabel: string;
  translateFn: (key: string) => string;
  variant?: RichTextMessageEditorVariant;
  isPreviewing?: boolean;
  previewContent?: ReactNode;
  includeHtml?: boolean;
  toolbarTrailingItems?: ReactNode;
  attachmentControl?: EmailAttachmentControlProps;
  footerTrailing?: ReactNode;
  footerPaddingY?: BoxProps['py'];
  afterFooter?: ReactNode;
  containerProps?: BoxProps;
  editableMinH?: string;
  editorMaxH?: string;
  showVariableControls?: boolean;
}

const EDITOR_THEME = {
  paragraph: 'editor-paragraph',
  text: {
    bold: 'editor-text-bold',
    italic: 'editor-text-italic',
    underline: 'editor-text-underline',
  },
  link: 'editor-link',
  heading: {
    h1: 'editor-heading-h1',
    h2: 'editor-heading-h2',
  },
  list: {
    ol: 'editor-list-ol',
    ul: 'editor-list-ul',
    listitem: 'editor-listitem',
  },
};

const EDITOR_NODES = [VariableNode, LinkNode, ListNode, ListItemNode, HeadingNode];
const DOCUMENT_TOOLBAR_INLINE_OFFSET = '-16px';
const EDITOR_FONT_SIZE = '14px';

export const normalizeEditorPlainText = (text: string): string => {
  return text.replace(/\r\n/g, '\n');
};

export const normalizeEditorRootPlainText = (blockTexts: string[]): string => {
  return normalizeEditorPlainText(blockTexts.join('\n'));
};

const getPlainTextFromRoot = (): string => {
  const blockTexts = $getRoot()
    .getChildren()
    .map((child) => child.getTextContent());

  return normalizeEditorRootPlainText(blockTexts);
};

const RichTextMessageEditor = memo(function RichTextMessageEditor({
  editorKey,
  namespace,
  initialContent,
  availableVariables,
  onContentChange,
  placeholder,
  ariaLabel,
  insertVariableLabel,
  translateFn,
  variant = 'card',
  isPreviewing = false,
  previewContent,
  includeHtml = false,
  toolbarTrailingItems,
  attachmentControl,
  footerTrailing,
  footerPaddingY,
  afterFooter,
  containerProps,
  editableMinH = '200px',
  editorMaxH,
  showVariableControls = true,
}: RichTextMessageEditorProps): ReactElement {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isDocument = variant === 'document';

  const editorConfig = useMemo(
    () => ({
      namespace,
      nodes: EDITOR_NODES,
      theme: EDITOR_THEME,
      onError: (error: Error) => {
        console.error('Lexical error:', error);
      },
    }),
    [namespace]
  );

  const handleEditorChange = useCallback(
    (editorState: EditorState, editor: LexicalEditor) => {
      if (isPreviewing) return;

      editorState.read(() => {
        const plainText = getPlainTextFromRoot();
        const html = includeHtml ? $generateHtmlFromNodes(editor) : undefined;

        onContentChange({
          editorState,
          editor,
          plainText,
          html,
        });
      });
    },
    [includeHtml, isPreviewing, onContentChange]
  );

  const resolvedAfterFooter = attachmentControl ? (
    <>
      {afterFooter}
      <EmailAttachmentsField
        {...attachmentControl}
        disabled={isPreviewing || attachmentControl.disabled}
        onRetry={() => fileInputRef.current?.click()}
      />
    </>
  ) : (
    afterFooter
  );

  const editorLineHeight = isDocument ? '1.65' : '1.7';

  return (
    <LexicalComposer key={editorKey} initialConfig={editorConfig}>
      <Box
        rounded={isDocument ? '0' : 'lg'}
        border={isDocument ? 'none' : '1px solid'}
        borderColor="border"
        bg={isDocument ? 'transparent' : 'bg'}
        flex={1}
        display="flex"
        flexDirection="column"
        minH="320px"
        overflow="hidden"
        {...containerProps}
      >
        <Box
          mx={isDocument ? 0 : 2}
          ml={isDocument ? DOCUMENT_TOOLBAR_INLINE_OFFSET : undefined}
          mt={isDocument ? 1.5 : 2}
          minW={0}
          bg={isDocument ? 'transparent' : 'gray.50'}
          rounded={isDocument ? 'none' : 'md'}
          border={isDocument ? 'none' : '1px solid'}
          borderColor={isDocument ? 'transparent' : 'gray.100'}
          overflow="hidden"
          css={{
            '& > div': {
              borderBottom: isDocument ? 'none' : undefined,
              paddingLeft: isDocument ? 0 : undefined,
              paddingRight: isDocument ? 0 : undefined,
            },
          }}
        >
          <ToolbarPlugin
            trailingItems={toolbarTrailingItems}
            attachmentControl={
              attachmentControl
                ? {
                    inputRef: fileInputRef,
                    onFileSelect: attachmentControl.onFileSelect,
                    accept: ACCEPTED_TYPES,
                    ariaLabel: translateFn('bulkEmail.compose.attach'),
                    disabled:
                      isPreviewing ||
                      attachmentControl.disabled ||
                      !!attachmentControl.deletingId ||
                      attachmentControl.attachments.length >= EMAIL_ATTACHMENT_LIMITS.maxCount,
                    isUploading: attachmentControl.isUploading,
                  }
                : undefined
            }
          />
        </Box>

        {isPreviewing && previewContent ? (
          <Box flex={1} minH={0} overflow="auto" px={isDocument ? 0 : 4} py={3}>
            {previewContent}
          </Box>
        ) : null}

        <Box
          flex={1}
          overflow="auto"
          position="relative"
          px={isDocument ? 0 : 4}
          py={3}
          maxH={editorMaxH ?? (isDocument ? undefined : '40vh')}
          minH={0}
          display={isPreviewing ? 'none' : undefined}
          onMouseDown={placeCaretFromFieldGutter}
          css={
            isDocument
              ? {
                  '& .editor-paragraph': {
                    marginTop: 0,
                    marginBottom: '0.85em',
                  },
                  '& .editor-paragraph:last-child': {
                    marginBottom: 0,
                  },
                }
              : undefined
          }
        >
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="editor-input"
                style={{
                  boxSizing: 'border-box',
                  minHeight: editableMinH,
                  outline: 'none',
                  fontSize: EDITOR_FONT_SIZE,
                  lineHeight: editorLineHeight,
                  position: 'relative',
                  zIndex: 0,
                }}
                aria-label={ariaLabel}
              />
            }
            placeholder={
              <Box
                position="absolute"
                top="12px"
                left={isDocument ? '0' : '16px'}
                color="fg.subtle"
                fontSize={EDITOR_FONT_SIZE}
                lineHeight={editorLineHeight}
                pointerEvents="none"
                userSelect="none"
              >
                {placeholder}
              </Box>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </Box>

        <OnChangePlugin onChange={handleEditorChange} ignoreSelectionChange />
        <HistoryPlugin />
        <LinkPlugin />
        <ListPlugin />
        {showVariableControls && (
          <VariableTypeaheadPlugin
            availableVariables={availableVariables}
            translateFn={translateFn}
          />
        )}
        <InitialStatePlugin initialContent={initialContent ?? null} />

        {!isPreviewing || resolvedAfterFooter ? (
          <VStack
            align="stretch"
            flexShrink={0}
            gap={resolvedAfterFooter ? 2.5 : 0}
            px={isDocument ? 0 : 2.5}
            py={footerPaddingY}
            pb={footerPaddingY ? undefined : isDocument ? (resolvedAfterFooter ? 2 : 0) : 2.5}
            pt={footerPaddingY ? undefined : isDocument ? 2 : 0}
            borderTop={isDocument ? '1px solid' : undefined}
            borderColor={isDocument ? 'border' : undefined}
            minH={isDocument ? '40px' : undefined}
          >
            {!isPreviewing && (
              <HStack
                justify={footerTrailing ? 'space-between' : 'flex-start'}
                align="center"
                gap={3}
                minW={0}
              >
                {showVariableControls && (
                  <VariableDropdownPlugin
                    availableVariables={availableVariables}
                    translateFn={translateFn}
                    insertVariableLabel={insertVariableLabel}
                  />
                )}
                {footerTrailing}
              </HStack>
            )}
            {resolvedAfterFooter}
          </VStack>
        ) : null}
      </Box>
    </LexicalComposer>
  );
});

export default RichTextMessageEditor;
